import { Link } from 'react-router-dom';
import { ArrowUpRight, Sparkles, Globe, Cpu, BarChart3, Compass } from 'lucide-react';
import AINetwork from '@/components/AINetwork';
import ServiceCard from '@/components/ServiceCard';
import CTABand from '@/components/CTABand';
import { SERVICES, INDUSTRIES, REGIONS, WHY_US, TESTIMONIALS } from '@/lib/content';
import { HERO, SECTION } from '@/lib/testIds';

export default function HomePage() {
  return (
    <div>
      {/* HERO */}
      <section data-testid={HERO.root} className="relative pt-32 pb-24 lg:pt-44 lg:pb-32 overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-40" aria-hidden />
        <div className="ai-network" aria-hidden />
        <AINetwork density={36} />
        <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-b from-transparent to-[#040914]" />

        <div className="relative max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full border border-cyan-400/20 bg-cyan-400/5 text-xs uppercase tracking-[0.22em] text-cyan-300 mb-8">
            <span className="w-1.5 h-1.5 rounded-full bg-cyan-400 animate-pulse-glow" />
            Global AI Growth Consultancy
          </div>

          <h1
            data-testid={HERO.headline}
            className="font-heading text-5xl sm:text-6xl lg:text-7xl xl:text-[5.5rem] font-semibold leading-[1.02] tracking-tighter max-w-5xl text-balance"
          >
            <span className="block">AI-Powered Growth</span>
            <span className="block">& <span className="gradient-text">Digital Transformation</span></span>
          </h1>

          <p
            data-testid={HERO.subhead}
            className="mt-8 max-w-2xl text-lg sm:text-xl text-white/70 leading-relaxed"
          >
            We help global businesses scale through performance marketing, AI automation,
            analytics, customer journey optimization, and digital transformation —
            engineered for measurable outcomes.
          </p>

          <div className="mt-10 flex flex-col sm:flex-row gap-4">
            <Link
              to="/contact"
              data-testid={HERO.primaryCta}
              className="btn-shimmer inline-flex items-center justify-center gap-2 px-7 py-4 rounded-full bg-cyan-400 text-[#040914] font-semibold hover:bg-cyan-300 transition-colors"
            >
              Book a Strategy Call
              <ArrowUpRight className="w-5 h-5" />
            </Link>
            <Link
              to="/services"
              data-testid={HERO.secondaryCta}
              className="inline-flex items-center justify-center gap-2 px-7 py-4 rounded-full border border-white/20 hover:border-cyan-400 hover:bg-white/5 transition-colors font-medium"
            >
              Explore Solutions
            </Link>
          </div>

          {/* Stat strip */}
          <div className="mt-20 grid grid-cols-2 md:grid-cols-4 gap-6 lg:gap-10 border-t border-white/10 pt-10">
            {[
              { k: '8+', v: 'Regions Served' },
              { k: '15+', v: 'Years of Senior Expertise' },
              { k: '40+', v: 'Brands Transformed' },
              { k: '3.2x', v: 'Avg. ROAS Improvement' },
            ].map((s, i) => (
              <div key={i} className="flex flex-col gap-1">
                <div className="font-heading text-3xl sm:text-4xl font-semibold gradient-text-static">{s.k}</div>
                <div className="text-xs uppercase tracking-[0.18em] text-brand-ink">{s.v}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* MISSION / VISION */}
      <section data-testid={SECTION.mission} className="relative py-24 lg:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            <div className="lg:col-span-4">
              <div className="text-xs uppercase tracking-[0.22em] text-cyan-300/80 mb-4">Mission & Vision</div>
              <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-semibold leading-tight">
                Engineering the next generation of growth.
              </h2>
            </div>
            <div className="lg:col-span-8 grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="glass rounded-2xl p-8">
                <Compass className="w-6 h-6 text-cyan-300 mb-5" strokeWidth={1.5} />
                <div className="text-xs uppercase tracking-[0.22em] text-brand-ink mb-2">Our Mission</div>
                <p className="text-lg text-white/85 leading-relaxed">
                  To help ambitious organizations compound growth by combining strategy, AI,
                  analytics and customer experience into a single operating system.
                </p>
              </div>
              <div className="glass rounded-2xl p-8">
                <Sparkles className="w-6 h-6 text-purple-400 mb-5" strokeWidth={1.5} />
                <div className="text-xs uppercase tracking-[0.22em] text-brand-ink mb-2">Our Vision</div>
                <p className="text-lg text-white/85 leading-relaxed">
                  A world where every business — from startup to enterprise — operates with
                  the intelligence, speed and precision of an AI-native company.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SERVICES OVERVIEW */}
      <section data-testid={SECTION.services} className="relative py-24 lg:py-32 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
            <div>
              <div className="text-xs uppercase tracking-[0.22em] text-cyan-300/80 mb-4">What we do</div>
              <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-semibold leading-tight max-w-2xl">
                A full-stack growth operating system.
              </h2>
            </div>
            <Link
              to="/services"
              className="inline-flex items-center gap-2 text-sm text-cyan-300 hover:text-cyan-200 group"
            >
              Explore all services
              <ArrowUpRight className="w-4 h-4 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
            </Link>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {SERVICES.map((s) => (<ServiceCard key={s.slug} service={s} />))}
          </div>
        </div>
      </section>

      {/* WHY US */}
      <section data-testid={SECTION.whyUs} className="relative py-24 lg:py-32 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="max-w-3xl mb-14">
            <div className="text-xs uppercase tracking-[0.22em] text-cyan-300/80 mb-4">Why SKV Digital Labs</div>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-semibold leading-tight">
              Operator-led. Outcome-bound. <span className="gradient-text-static">AI-native.</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {WHY_US.map((w, i) => (
              <div key={i} className="group relative rounded-2xl border border-white/10 bg-[#0B1221]/40 p-7 hover:border-cyan-400/30 transition-all duration-300">
                <div className="font-mono-display text-xs text-cyan-300/70 mb-4">0{i + 1}</div>
                <h3 className="font-heading text-xl font-medium mb-3">{w.title}</h3>
                <p className="text-sm text-white/60 leading-relaxed">{w.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* INDUSTRIES */}
      <section data-testid={SECTION.industries} className="relative py-24 lg:py-32 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            <div className="lg:col-span-5">
              <div className="text-xs uppercase tracking-[0.22em] text-cyan-300/80 mb-4">Industries served</div>
              <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-semibold leading-tight mb-6">
                Sectors we transform.
              </h2>
              <p className="text-white/65 leading-relaxed max-w-md">
                Deep playbooks across high-growth and regulated industries — engineered for
                each market&apos;s economics, buyer journey and competitive intensity.
              </p>
            </div>
            <div className="lg:col-span-7 grid grid-cols-2 gap-3">
              {INDUSTRIES.map((ind, i) => (
                <div key={i} className="flex items-center gap-3 rounded-xl border border-white/10 bg-white/[0.02] px-5 py-4 hover:border-cyan-400/30 transition-colors">
                  <div className="w-1.5 h-1.5 rounded-full bg-gradient-to-r from-cyan-400 to-purple-400" />
                  <span className="font-medium text-sm">{ind}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* GLOBAL EXPERTISE */}
      <section data-testid={SECTION.global} className="relative py-24 lg:py-32 border-t border-white/5 overflow-hidden">
        <AINetwork density={22} />
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="text-center max-w-3xl mx-auto mb-14">
            <div className="text-xs uppercase tracking-[0.22em] text-cyan-300/80 mb-4">Global expertise</div>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-semibold leading-tight">
              One playbook. <span className="gradient-text-static">Eight markets.</span>
            </h2>
            <p className="mt-5 text-white/65">
              Active engagements and cross-market intelligence across four continents.
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3">
            {REGIONS.map((r) => (
              <div key={r.code} className="group rounded-xl border border-white/10 bg-[#0B1221]/40 p-5 text-center hover:border-cyan-400/30 hover:-translate-y-0.5 transition-all">
                <div className="font-mono-display text-xs text-cyan-300/70 mb-2">{r.code}</div>
                <div className="font-medium text-sm">{r.name}</div>
              </div>
            ))}
          </div>

          <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-5">
            <div className="glass rounded-2xl p-6">
              <Globe className="w-6 h-6 text-cyan-300 mb-4" strokeWidth={1.5} />
              <h4 className="font-heading text-lg mb-2">Cross-Market Playbooks</h4>
              <p className="text-sm text-white/60">Patterns proven across mature and emerging markets.</p>
            </div>
            <div className="glass rounded-2xl p-6">
              <Cpu className="w-6 h-6 text-purple-300 mb-4" strokeWidth={1.5} />
              <h4 className="font-heading text-lg mb-2">AI-Native Toolchain</h4>
              <p className="text-sm text-white/60">Modern data stack, agents, MMM, server-side tracking.</p>
            </div>
            <div className="glass rounded-2xl p-6">
              <BarChart3 className="w-6 h-6 text-cyan-300 mb-4" strokeWidth={1.5} />
              <h4 className="font-heading text-lg mb-2">Defensible Measurement</h4>
              <p className="text-sm text-white/60">Privacy-resilient analytics built to survive scrutiny.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Mini Testimonials Strip */}
      <section className="relative py-20 border-t border-white/5 overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 mb-10">
          <div className="text-xs uppercase tracking-[0.22em] text-cyan-300/80 mb-4">Trusted by growth leaders</div>
          <h2 className="font-heading text-3xl sm:text-4xl font-semibold">What clients are saying.</h2>
        </div>
        <div className="overflow-hidden">
          <div className="flex gap-5 animate-marquee w-max">
            {[...TESTIMONIALS, ...TESTIMONIALS].map((t, i) => (
              <div key={i} className="w-[380px] shrink-0 rounded-2xl border border-white/10 bg-[#0B1221]/60 p-6">
                <p className="text-sm text-white/75 leading-relaxed">&ldquo;{t.quote}&rdquo;</p>
                <div className="mt-5 pt-4 border-t border-white/5">
                  <div className="font-medium text-sm">{t.name}</div>
                  <div className="text-xs text-brand-ink mt-0.5">{t.title} · {t.region}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <CTABand />
    </div>
  );
}
